import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { useQuery } from "@tanstack/react-query";

import { env } from "@/config";

import { userCache } from "../auth";

export interface UseFeatureFlagOptions<T> {
    cacheTtl?: number;
    isEnabled?: boolean;
    onResponse?: (flag: T | undefined) => void;
}

export const useFeatureFlag = <T = boolean>(
    featureFlag: string,
    context: Record<string, unknown> = {},
    { cacheTtl = 1800 /* 30 min */, isEnabled = true, onResponse }: UseFeatureFlagOptions<T> = {},
) => {
    // fall-back to identify (sub) if employeeId is undefined
    const employeeId = userCache.employeeId ?? userCache.identity;
    const { data, isLoading, isError } = useQuery({
        queryKey: [featureFlag, ...Object.values(context)],
        queryFn: async (): Promise<T | undefined> => {
            const result = await getFeature<T>(
                featureFlag,
                {
                    environment: env.mode,
                    channel: env.channel,
                    employeeId,
                    appName: "expert-workspace",
                    source: "expert-ui",
                    ...context,
                },
                cacheTtl,
            );
            onResponse?.(result);
            return result;
        },
        refetchOnMount: false,
        enabled: isEnabled,
    });
    return { data, isLoading, isError };
};
